import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, HelpCircle, Phone, User, Key, Wallet, X, Info } from 'lucide-react';
import { useCoreController } from '@/services/coreController';
import { directusService } from '@/services/directus';
import { storage } from '@/lib/storage';
import { analyticsService } from '@/services/analytics';
import { getAccountColor } from '@/constants/theme';
import { Notification } from '@/components/Notification';

interface WithdrawPageState {
  amount: number;
  accountType: string;
}

interface WithdrawForm {
  user_id: string;
  phone: string;
  name: string;
  code: string;
}

export function WithdrawPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const coreController = useCoreController();
  const state = location.state as WithdrawPageState;

  const [isLoading, setIsLoading] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [formData, setFormData] = useState<WithdrawForm>({
    user_id: storage.read<string>(`user_id_${coreController.compte.type}`) || '',
    phone: storage.read<string>('phone') || '',
    name: storage.read<string>('name') || '',
    code: storage.read<string>('withdrawal_code') || '',
  });
  const [errors, setErrors] = useState<Partial<WithdrawForm>>({});
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    message: string;
    type: 'error' | 'success';
  }>({
    isVisible: false,
    message: '',
    type: 'error',
  });

  useEffect(() => {
    if (!state) {
      navigate('/');
      return;
    }

    analyticsService.trackScreenView('withdraw_page');
  }, [state]);

  const themeColor = getAccountColor(coreController.compte.type);

  const showNotification = (message: string, type: 'error' | 'success' = 'error') => {
    setNotification({
      isVisible: true,
      message,
      type,
    });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  const handleInputChange = (field: keyof WithdrawForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    storage.write(field === 'user_id' ? `user_id_${coreController.compte.type}` : field, value);

    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<WithdrawForm> = {};

    if (!formData.user_id.trim()) {
      newErrors.user_id = 'Ce champ est requis';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Ce champ est requis';
    } else if (formData.phone.length !== 8) {
      newErrors.phone = 'Le numéro de téléphone doit contenir 8 chiffres';
    } else if (!/^\d+$/.test(formData.phone)) {
      newErrors.phone = 'Seuls les chiffres sont autorisés';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Ce champ est requis';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Ce champ est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showNotification('Veuillez remplir tous les champs requis');
      analyticsService.trackError(
        new Error('Withdrawal form validation failed'),
        'withdraw_page'
      );
      return;
    }

    setIsLoading(true);

    try {
      // Track withdrawal initiation
      await analyticsService.trackWithdrawalInitiated({
        amount: state.amount,
        accountType: coreController.compte.type || '',
        commercialId: coreController.commercialId || undefined,
      });

      // Process withdrawal
      await directusService.processWithdrawal({
        code: formData.code,
        user_id: formData.user_id,
        phone: formData.phone,
        name: formData.name,
        compte_id: coreController.compte.id || '',
      });

      showNotification('Demande de retrait initiée avec succès!', 'success');

      // Track successful withdrawal
      analyticsService.trackEvent('withdrawal_success', {
        amount: state.amount,
        accountType: coreController.compte.type,
      });

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error: any) {
      console.error('Error during withdrawal:', error);

      // Afficher le message d'erreur exact (comme Flutter)
      const errorMessage = error.message || 'Une erreur s\'est produite lors du retrait';
      showNotification(errorMessage);

      analyticsService.trackError(
        error,
        'withdraw_page_processing'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!state) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* App Bar */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow"
            style={{ color: themeColor }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1
            className="flex-1 text-center text-lg font-semibold"
            style={{ color: themeColor }}
          >
            Retrait {coreController.compte.type}
          </h1>
          <button
            onClick={() => setShowInstructions(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold shadow-md transition-all active:scale-95"
          >
            <HelpCircle className="w-5 h-5" strokeWidth={3} />
            <span className="text-xs">Comment retirer ?</span>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Instruction Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-2xl p-4 border"
          style={{
            background: `linear-gradient(135deg, ${themeColor}1A, ${themeColor}0D)`,
            borderColor: `${themeColor}33`,
          }}
        >
          <div className="flex flex-col items-center text-center">
            <Info className="w-7 h-7 mb-3" style={{ color: themeColor }} />
            <h3 className="font-semibold mb-2" style={{ color: themeColor }}>
              Instructions pour le retrait
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              Sélectionnez {(coreController.appSettingResponse.config?.WITHDRAW_RUE as Record<string, string>)[coreController.compte.type]} comme point de retrait lors de la demande de retrait dans votre application{' '}
              {coreController.compte.type}.
            </p>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="space-y-5"
        >
          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-4">
              Informations de retrait
            </h3>

            {/* User ID */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID {coreController.compte.type}
              </label>
              <div className="relative">
                <div
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                  style={{ backgroundColor: `${themeColor}1A` }}
                >
                  <User className="w-5 h-5" style={{ color: themeColor }} />
                </div>
                <input
                  type="text"
                  value={formData.user_id}
                  onChange={(e) => handleInputChange('user_id', e.target.value)}
                  placeholder="Entrez votre ID de compte"
                  className={`w-full pl-14 pr-4 py-3 rounded-xl border-2 transition-all bg-white ${
                    errors.user_id
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-[var(--theme-color)]'
                  } focus:outline-none`}
                  style={!errors.user_id ? ({ '--theme-color': themeColor } as any) : undefined}
                />
              </div>
              {errors.user_id && (
                <p className="mt-1 text-sm text-red-600">{errors.user_id}</p>
              )}
            </div>

            {/* Phone */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de téléphone Orange money
              </label>
              <div className="relative">
                <div
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                  style={{ backgroundColor: `${themeColor}1A` }}
                >
                  <Phone className="w-5 h-5" style={{ color: themeColor }} />
                </div>
                <input
                  type="tel"
                  maxLength={8}
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Numéro de téléphone pour recevoir le dépôt"
                  className={`w-full pl-14 pr-4 py-3 rounded-xl border-2 transition-all bg-white ${
                    errors.phone
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-[var(--theme-color)]'
                  } focus:outline-none`}
                  style={!errors.phone ? ({ '--theme-color': themeColor } as any) : undefined}
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* Name */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Nom sur le compte Orange Money
              </label>
              <div className="relative">
                <div
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                  style={{ backgroundColor: `${themeColor}1A` }}
                >
                  <User className="w-5 h-5" style={{ color: themeColor }} />
                </div>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Nom sur le compte Orange money recevant le dépôt"
                  className={`w-full pl-14 pr-4 py-3 rounded-xl border-2 transition-all bg-white ${
                    errors.name
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-[var(--theme-color)]'
                  } focus:outline-none`}
                  style={!errors.name ? ({ '--theme-color': themeColor } as any) : undefined}
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Code */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code de retrait
              </label>
              <div className="relative">
                <div
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                  style={{ backgroundColor: `${themeColor}1A` }}
                >
                  <Key className="w-5 h-5" style={{ color: themeColor }} />
                </div>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  placeholder="Entrez votre code de retrait"
                  className={`w-full pl-14 pr-4 py-3 rounded-xl border-2 transition-all bg-white ${
                    errors.code
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-[var(--theme-color)]'
                  } focus:outline-none`}
                  style={!errors.code ? ({ '--theme-color': themeColor } as any) : undefined}
                />
              </div>
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2
                       shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: themeColor }}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Wallet className="w-5 h-5" />
                  <span>Retrait</span>
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>

      {/* Instructions Dialog */}
      <AnimatePresence>
        {showInstructions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowInstructions(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-md w-full p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <HelpCircle style={{ color: themeColor }} />
                  <h2 className="text-lg font-semibold" style={{ color: themeColor }}>
                    Instructions pour le retrait
                  </h2>
                </div>
                <button
                  onClick={() => setShowInstructions(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-3">
                {[
                  `Ouvrez l'application ${coreController.compte.type}`,
                  'Allez dans la section Retrait',
                  'Sélectionnez Espèces comme méthode de retrait',
                  'Choisissez Ouagadougou comme ville',
                  `Recherchez et sélectionnez ${(coreController.appSettingResponse.config?.WITHDRAW_RUE as Record<string, string>)[coreController.compte.type] ?? "EASYSERVICE EXPRESS"} comme point de retrait`,
                  'Suivez les instructions affichées pour finaliser l\'opération et obtenir le code de retrait',
                ].map((step, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: themeColor }}
                    >
                      <span className="text-white text-xs font-bold">{index + 1}</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowInstructions(false)}
                className="w-full mt-6 py-3 rounded-xl font-semibold transition-colors"
                style={{ backgroundColor: themeColor, color: 'white' }}
              >
                Fermer
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification */}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
}

export default WithdrawPage;
